<template>
  <div class="editorial-upload">
    <div class="editorial-upload__header">
      <div class="editorial-upload__header-info">
        {{ $t('Add files to the upload queue and click the Start Upload button.') }}
        <span
          ref="infoButtonWrapper"
          :class="{ 'editorial-upload__info-button-wrapper--file-types': fileTypesPopoverVisible }"
          class="editorial-upload__info-button-wrapper"
          aria-live="polite"
        >
          <button
            :aria-label="$t('Permitted File Types')"
            class="editorial-upload__info-button nebula-button nebula-button--icon-only"
            @click="fileTypesPopoverVisible = !fileTypesPopoverVisible"
          >
            <NebulaIcon
              class="comet-button__icon comet-button__icon--left"
              symbol-id="information"
              size="m"
            />
            <div
              class="comet-popover comet-popover--bottom-right editorial-upload__info-tooltip"
              aria-hidden="true"
            >
              <div class="comet-popover__nub" />
              {{ $t('Permitted File Types') }}
            </div>
          </button>
          <div
            v-if="fileTypesPopoverVisible"
            class="comet-popover comet-popover--bottom editorial-upload__info-dropdown"
          >
            <div class="comet-popover__nub" />
            <!-- Temporarily removing ability to upload videos
            <p>
              <strong>{{ $t('Videos:') }}</strong>
              {{ $t('mp4') }}
            </p>
            -->
            <p>
              <strong>{{ $t('Images:') }}</strong>
              {{ $t('gif, jpg, jpeg, png') }}
            </p>
            <p>
              <strong>{{ $t('Audio:') }}</strong>
              {{ $t('mp3') }}
            </p>
          </div>
        </span>
      </div>
      <div class="editorial-upload__header-actions">
        <button
          ref="addFileButton"
          class="nebula-button nebula-button--s nebula-button--fill"
          :disabled="!canAddFile"
          @click="onAddFileClick"
        >
          <NebulaIcon
            symbol-id="plus"
            size="s"
          />
          {{ $t('Add File') }}
        </button>
        <input
          type="file"
          ref="fileInput"
          class="editorial-upload__file-input"
          :multiple="allowMultiple"
          @change="onFilesChanged"
          :disabled="busy"
        >
      </div>
    </div>
    <div
      ref="content"
      :class="{ 'editorial-upload__content--empty': !items.length }"
      class="editorial-upload__content"
    >
      <div v-if="!items.length">
        {{ $t('Add a file to get started.') }}
      </div>
      <template v-else>
        <table
          :class="{ 'editorial-upload-table--has-scroll': uploadTableHasScrollbar }"
          class="comet-data-table editorial-upload-table"
        >
          <thead class="comet-data-table__head">
            <th class="comet-data-table__header editorial-upload-table__filename">
              {{ $t('FILENAME') }}
            </th>
            <th class="comet-data-table__header editorial-upload-table__size">
              {{ $t('SIZE') }}
            </th>
            <th class="comet-data-table__header editorial-upload-table__status">
              {{ $t('STATUS') }}
            </th>
            <th class="comet-data-table__header editorial-upload-table__remove">
              {{ $t('REMOVE') }}
            </th>
          </thead>
        </table>
        <div
          ref="uploadTableContainer"
          class="editorial-upload__table-scroll-container"
        >
          <table class="comet-data-table editorial-upload-table">
            <tbody>
              <tr
                v-for="item in items"
                :key="item.id"
                ref="items"
                class="comet-data-table__row editorial-upload-table__row"
                :class="{'editorial-upload-table__row--error': item.error }"
              >
                <td
                  :data-label="$t('FILENAME')"
                  class="comet-data-table__cell editorial-upload-table__filename"
                >
                  <div class="editorial-upload-table__info-button-wrapper">
                    <button
                      v-if="item.error"
                      :aria-label="$t('File upload error')"
                      aria-describedby="editorial-upload__item-error-popover"
                      class="editorial-upload__info-button editorial-upload-table__info-button
                        nebula-button nebula-button--icon-only"
                    >
                      <NebulaIcon
                        class="comet-button__icon comet-button__icon--left"
                        symbol-id="information"
                        size="m"
                      />
                      <div
                        id="editorial-upload__item-error-popover"
                        class="comet-popover comet-popover--bottom
                          editorial-upload__info-tooltip editorial-upload-table__info-tooltip"
                        aria-hidden="true"
                      >
                        <div class="comet-popover__nub" />
                        <template v-if="item.error.fileExtension">
                          <!-- eslint-disable-next-line max-len -->
                          {{ $t(`Error: .%(ext)s types cannot be added in Studio. Please upload in`, { ext: item.error.fileExtension }) }}
                          <a
                            :href="contentUrl"
                            class="editorial-upload__content-link"
                            target="_blank"
                          >
                            {{ contentUrl }}
                          </a>
                        </template>
                        <template v-else>
                          {{ $t('An unexpected error occurred. Please remove.') }}
                        </template>
                      </div>
                    </button>
                    {{ item.file.name }}
                  </div>
                </td>
                <td
                  :data-label="$t('SIZE')"
                  class="comet-data-table__cell editorial-upload-table__size"
                >
                  {{ formatFileSize(item.file.size) }}
                </td>
                <td
                  :data-label="$t('STATUS')"
                  class="comet-data-table__cell editorial-upload-table__status"
                >
                  {{ $t('%(status)d%', { status: item.status }) }}
                </td>
                <td
                  :data-label="$t('REMOVE')"
                  class="comet-data-table__cell editorial-upload-table__remove"
                >
                  <button
                    class="nebula-button nebula-button--s nebula-button--icon-only
                      editorial-upload-table__remove-file"
                    :aria-label="$t('Remove file')"
                    :disabled="busy"
                    @click="removeItem(item)"
                  >
                    <NebulaIcon
                      symbol-id="x"
                      size="s"
                    />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { get, first, throttle } from 'lodash-es';
import { ResizeSensor } from 'css-element-queries';
import { NebulaIcon } from '@discoveryedu/nebula-components';

const ASSET_TYPE_IMAGE = '1aa883f4-b310-4a5a-9d35-d07dc8cadf6c';
const ASSET_TYPE_AUDIO = '9c86af9f-5420-4662-abed-910857f7e301';
const ASSET_TYPE_VIDEO = 'b373b9c1-fe6d-4891-9ff3-3b59b23550e2';

const IMAGE_EXTENSIONS_WITH_THUMBNAILS = ['jpg', 'jpeg', 'png'];
const IMAGE_EXTENSIONS = [
  'gif', ...IMAGE_EXTENSIONS_WITH_THUMBNAILS,
];
const VIDEO_EXTENSIONS = [];
const AUDIO_EXTENSIONS = ['mp3'];

const ALL_EXTENSIONS = IMAGE_EXTENSIONS.concat(VIDEO_EXTENSIONS).concat(AUDIO_EXTENSIONS);

const MEDIA_FILE_FORMATS = [
  {
    extensions: ['mp4'],
    formats: [
      {
        formatId: 34, // 300k MP4 (mp4, m4v, 3gp)
        maxSizeBytes: 300000, // <= 300KB
      },
      {
        formatId: 35, // 2Mb MP4 (mp4, m4v, 3gp)
        maxSizeBytes: Infinity, // > 300KB
      },
    ],
  },
  {
    extensions: ['mp3'],
    formats: [
      {
        formatId: 16, // MP3 (mp3)
      },
    ],
  },
  {
    extensions: ['gif', 'jpg', 'jpeg', 'png'],
    formats: [
      {
        formatId: 38, // Large Images (.gif,.jpg,.png)
      },
    ],
  },
];

const ASSET_STATUS_ACTIVE = 1;

export default {
  components: {
    NebulaIcon,
  },
  props: {
    /*
      Should be environment specific e.g. https://atlas.local.discoveryeducation.com
      or https://atlas.discoveryeducation.com for prod.
    */
    contentUrl: {
      type: String,
      required: true,
    },
    tokens: {
      type: Object,
      required: true,
    },
    localeCode: {
      type: String,
      default: 'en',
    },
    allowMultiple: {
      type: Boolean,
      default: true,
    },
    initFiles: {
      type: Array,
      default: null,
    },
  },
  emits: [
    'busy',
    'can-upload',
  ],
  data() {
    return {
      fileTypesPopoverVisible: false,
      items: [],
      busy: false,
      fileSizeUnits: [
        { abbr: this.$t('B') },
        { abbr: this.$t('kB') },
        { abbr: this.$t('MB') },
        { abbr: this.$t('GB') },
      ],
      uploadTableHasScrollbar: false,
    };
  },
  created() {
    // It's possible to open the modal with files already selected (e.g. when
    // pasting from the clipboard). We only need to read these once.
    if (this.initFiles) {
      this.items = [...this.initFiles].map((file) => ({
        id: uuid(),
        file,
        status: 0,
        error: null,
        complete: false,
      }));
    }

    this.throttleContentResized = throttle(() => {
      this.setUploadTableHasScrollbar();
    }, 50);
  },
  mounted() {
    document.addEventListener('click', this.onBodyClick);
    this.contentResizeSensor = new ResizeSensor(
      this.$refs.content,
      this.throttleContentResized,
    );
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onBodyClick);
    if (this.contentResizeSensor) {
      ResizeSensor.detach(this.$refs.content);
    }
  },
  watch: {
    canUpload() {
      this.$emit('can-upload', this.canUpload);
    },
    busy() {
      this.$emit('busy', this.busy);
    },
  },
  computed: {
    canUpload() {
      return !this.busy
        && !this.hasErrors
        && this.items.length;
    },
    canAddFile() {
      return !this.busy
        && !this.hasErrors
        && (this.allowMultiple || !this.items.length);
    },
    hasErrors() {
      return this.items.some((item) => item.error);
    },
  },
  methods: {
    onBodyClick({ target }) {
      if (!this.$refs.infoButtonWrapper.contains(target)) {
        this.fileTypesPopoverVisible = false;
      }
    },
    onFilesChanged(event) {
      Array.from(event.target.files).forEach((file) => {
        this.items.push({
          id: uuid(),
          file,
          status: 0,
          error: null,
          complete: false,
        });
      });
      this.setUploadTableHasScrollbar();
      this.$refs.fileInput.value = '';
    },
    onAddFileClick() {
      this.$refs.fileInput.click();
    },
    removeItem(item) {
      const index = this.items.findIndex((itm) => itm.id === item.id);
      this.items.splice(index, 1);
      this.setUploadTableHasScrollbar();
    },
    formatFileSize(bytes) {
      if (!bytes) return null;
      const power = Math.floor(Math.log(bytes) / Math.log(1000));
      const size = bytes / (1000 ** power);
      const unit = this.fileSizeUnits[power];
      return `${size.toFixed()}${unit.abbr}`;
    },
    setUploadTableHasScrollbar() {
      this.$nextTick(() => {
        if (!this.$refs.uploadTableContainer) return;
        this.uploadTableHasScrollbar = this.$refs.uploadTableContainer.clientHeight
          < this.$refs.uploadTableContainer.scrollHeight;
      });
    },
    apiUrl(path) {
      return `${this.contentUrl}${path}`;
    },
    getAssetTypeId(fileExtension) {
      if (IMAGE_EXTENSIONS.includes(fileExtension)) return ASSET_TYPE_IMAGE;
      if (VIDEO_EXTENSIONS.includes(fileExtension)) return ASSET_TYPE_VIDEO;
      if (AUDIO_EXTENSIONS.includes(fileExtension)) return ASSET_TYPE_AUDIO;
      return null;
    },
    getFormatId(file) {
      const ext = this.getExtension(file.name);
      const mediaFileFormat = MEDIA_FILE_FORMATS.find((fmt) => fmt.extensions.includes(ext));
      if (!mediaFileFormat) return null;
      const format = mediaFileFormat.formats.length > 1
        ? mediaFileFormat.formats.find((fmt) => file.size <= fmt.maxSizeBytes)
        : first(mediaFileFormat.formats);
      return format?.formatId;
    },
    getExtension(filename) {
      const parts = filename.split('.');
      return parts.length > 1
        ? filename.split('.').pop().toLowerCase()
        : null;
    },
    getFileName(filename) {
      const ext = this.getExtension(filename);
      if (ext === 'jpeg') {
        const parts = filename.split('.');
        parts.pop();
        parts.push('jpg');
        return parts.join('.');
      }
      return filename;
    },
    reportProgress(item, percent) {
      const uploadItem = this.items.find((i) => i === item);
      if (uploadItem) uploadItem.status = percent;
    },
    createAsset(item) {
      const url = this.apiUrl('/v1/assets/create');

      // The content asset api expects language codes with hyphens, but the user language
      // code uses underscores
      const formattedLocaleCode = this.localeCode.replace(/_/g, '-');

      const payload = {
        title: this.getFileName(item.file.name),
        asset_type_id: this.getAssetTypeId(this.getExtension(item.file.name)),
        language_code: formattedLocaleCode,
        status_id: ASSET_STATUS_ACTIVE,
      };
      return axios.post(url, payload, {
        withCredentials: true,
        headers: {
          'X-Token': this.tokens?.createAsset,
        },
      }).then((response) => {
        this.reportProgress(item, 10);
        return response;
      });
    },
    createMediaFile(item) {
      const url = this.apiUrl('/api/v1/media_files/create');
      const payload = new FormData();
      payload.append('asset_id', item.asset.id);
      payload.append('file_name', this.getFileName(item.file.name));
      payload.append('friendly_file_name', '');
      payload.append('format_id', this.getFormatId(item.file));
      return axios.post(url, payload, {
        withCredentials: true,
        headers: {
          'X-Token': this.tokens?.createMediaFile,
        },
      }).then((response) => {
        this.reportProgress(item, 20);
        return response;
      });
    },
    uploadMediaFile(item, presignedUrl) {
      const payload = new FormData();
      Object.keys(presignedUrl.fields).forEach((key) => {
        payload.append(key, presignedUrl.fields[key]);
      });
      payload.append('file', item.file);
      return axios.post(presignedUrl.url, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progress) => {
          // Allow file upload to take up 70 percentage points of total progress
          const pctPoints = Math.round((progress.loaded / progress.total) * 70);
          this.reportProgress(item, 20 + pctPoints);
        },
      });
    },
    uploadMediaFileComplete(item, mediaFileId) {
      const url = this.apiUrl('/api/v1/media_files/upload_complete');
      const payload = new FormData();
      payload.append('media_file_id', mediaFileId);
      return axios.post(url, payload, {
        withCredentials: true,
        headers: {
          'X-Token': this.tokens?.uploadMediaFileComplete,
        },
      }).then(() => {
        this.reportProgress(item, 100);
      });
    },
    generateThumbnails(item) {
      const url = this.apiUrl(`/api/v1/thumbnails/${item.asset.id}`);
      const payload = new FormData();
      payload.append('image', item.file);
      return axios.post(url, payload, {
        withCredentials: true,
        headers: {
          'X-Token': this.tokens?.generateThumbnails,
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    validate(item) {
      const ext = this.getExtension(item.file.name);
      if (!ALL_EXTENSIONS.includes(ext)) {
        const error = new Error(this.$t('File extension not supported'));
        error.fileExtension = ext;
        throw error;
      }
    },
    canGenerateThumbnails(item) {
      const ext = this.getExtension(item.file.name);
      return item.asset.type.id === ASSET_TYPE_IMAGE
        && IMAGE_EXTENSIONS_WITH_THUMBNAILS.includes(ext);
    },
    async uploadFile(item) {
      const uploadItem = this.items.find((i) => i === item);
      try {
        this.validate(item);

        const assetResponse = await this.createAsset(item);
        uploadItem.asset = assetResponse.data.asset;

        const mediaFileResponse = await this.createMediaFile(item);
        const {
          presigned_url: presignedUrl,
          media_file_id: mediaFileId,
        } = mediaFileResponse.data;

        if (!presignedUrl || !mediaFileId) {
          throw new Error(this.$t('File failed to upload.'));
        }

        const uploadPromises = [this.uploadMediaFile(item, presignedUrl)];
        if (this.canGenerateThumbnails(item)) {
          uploadPromises.push(this.generateThumbnails(item));
        }
        await Promise.all(uploadPromises);

        await this.uploadMediaFileComplete(item, mediaFileId);

        uploadItem.complete = true;
      } catch (error) {
        uploadItem.error = error;
        throw error;
      }
    },
    scrollErrorsIntoView() {
      const firstErrorIndex = this.items.findIndex((item) => item.error);
      const errorRowEl = get(this, `$refs.items[${firstErrorIndex}]`);
      if (errorRowEl) {
        errorRowEl.scrollIntoView(true);
      }
    },
    async upload() {
      try {
        this.busy = true;
        const uploadPromises = this.items.filter((item) => !item.complete && !item.error)
          .map((item) => this.uploadFile(item));
        await Promise.allSettled(uploadPromises);
        const errorItem = this.items.find((item) => item.error);
        if (errorItem) throw errorItem.error;
      } catch (error) {
        this.scrollErrorsIntoView();
        throw error;
      } finally {
        this.busy = false;
      }
      return this.items.map((item) => item.asset);
    },
  },
};
</script>

<style lang="stylus">
.editorial-upload {
  width: 100%;

  &__header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  &__content {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    height: 300px;
    justify-content: center;
    margin: $nebula-space-3x 0 0 0;

    &--empty {
      align-items: center;
    }

    a.editorial-upload__content-link {
      color: $nebula-color-white;
    }
  }

  &__header-info {
    align-items: center;
    display: flex;
  }

  &__header-actions {
    svg {
      fill: $nebula-color-white;
      margin-inline-end: $nebula-space-1x;
    }
    margin-inline-start: $nebula-space-2x;
  }

  &__info-button {
    background-color: transparent;
    border-radius: 50%;
    display: inline-block;
    padding: $nebula-space-half;
    position: relative;

    &:hover, &:focus {
      background-color: $nebula-color-platform-neutral-200;
    }

    &:active {
      background-color: $nebula-color-platform-neutral-400;
    }

    .editorial-upload__info-tooltip {
      white-space: nowrap;
    }
  }

  &__info-button-wrapper--file-types .editorial-upload__info-dropdown,
  &__info-button:hover .editorial-upload__info-tooltip,
  &__info-button:focus .editorial-upload__info-tooltip {
    display: block;
    opacity: 1;
    visibility: visible;
  }

  .comet-popover {
    font-size: $nebula-font-size-caption;
    text-align: start;
    top: 38px;

    &--bottom {
      transform: translateX(calc(-50% + 12px));
    }

    &--bottom-right {
      transform: translateX(-1px);
    }

    &--bottom-left {
      transform: translateX(calc(-100% + 26px));
    }
  }

  &__info-button-wrapper {
    display: inline-block;
    margin-inline-start: $nebula-space-1x;
    position: relative;

    &--file-types {
      .comet-popover.editorial-upload__info-tooltip {
        display: none;
      }

      .comet-popover.editorial-upload__info-dropdown {
        display: block;
      }
    }

    .editorial-upload__info-dropdown {
      width: 250px;

      p:first-of-type {
        margin-top: 0;
      }

      p:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  &__file-input {
    display: none;
  }

  &__table-scroll-container {
    height: "calc(100% - %s)" % $nebula-space-3x;
    overflow-y: auto;
    width: 100%;
  }

  html[dir="rtl"] & {
    .comet-popover--bottom {
      transform: translateX(calc(50% - 12px));
    }

    &__info-tooltip .comet-popover__nub {
      color: red;
      left: auto;
      right: $nebula-space-1x;
    }
  }
}

.editorial-upload-table {
  margin: 0;
  table-layout: fixed;
  width: 100%;

  &__info-button-wrapper {
    align-items: center;
    display: flex;
  }

  &__info-button {
    margin-inline-end: $nebula-space-1x;

    .editorial-upload-table__info-tooltip {
      white-space: normal;
      width: 175px;
    }
  }

  &__row--error {
    td {
      color: $comet-color-error-default;

      .editorial-upload-table__info-button {
        svg {
          fill: $comet-color-error-default;
        }
      }
    }
  }

  td:before {
    margin-inline-end: $nebula-space-2x;
    white-space: nowrap;
  }

  .editorial-upload-table__filename {
    word-break: break-word;
  }

  .editorial-upload-table__remove {
    text-align: end;
  }

  @media screen and (min-width: $comet-breakpoints-tablet-landscape) {
    .editorial-upload-table__filename {
      width: 50%;
    }

    .editorial-upload-table__size {
      width: 20%;
    }

    .editorial-upload-table__status {
      width: 15%;
    }

    .editorial-upload-table__remove {
      width: 15%;
    }

    &__info-button {
      .editorial-upload-table__info-tooltip {
        transform: translateX(-4px);
        width: 250px;

        .comet-popover__nub {
          inset-inline-start: $nebula-space-2x;
        }
      }
    }
  }

  &__remove-file {
    background-color: $nebula-color-error;
    display: inline-block;

    &:focus, &:hover, &:active {
      background-color: $nebula-color-error;
    }

    &:disabled {
      opacity: 0.3;
    }

    svg {
      display: block;
      fill: $nebula-color-white;
    }
  }

  &--has-scroll {
    width: calc(100% - 17px);
  }

  html[dir="rtl"] & {
    .editorial-upload-table__remove {
      text-align: end;
    }
  }
}
</style>
